import React, { useMemo } from "react";
import { AuditTrailResponse } from "@bluecrew/finance-service-node-client";
import { ShiftThresholds, TimesheetsShiftInfo } from "../../../types/Shift.types";
import { AuditClockInOutSection } from "./AuditClockInOutSection";
import { AuditBreakSection } from "./AuditBreakSection";
import { buildOriginalTimestamps } from "./utils/auditTrailUtils";
import { transformAuditResponse } from "./utils/transformAuditData";
import { AuditTrailScrollableContainer } from "./styledComponents";

export type AuditTrailDisplayProps = {
  data: AuditTrailResponse;
  timezone: string;
  shiftThresholds: ShiftThresholds;
  shiftKey: string;
  shiftInfo: TimesheetsShiftInfo;
};

/**
 * Main component for displaying the audit trail.
 * Composes clock in/out section and individual break sections.
 * Handles transformation of raw audit data into display format.
 */
export const AuditTrailDisplay = ({
  data,
  timezone,
  shiftThresholds,
  shiftKey,
  shiftInfo,
}: AuditTrailDisplayProps) => {
  const transformedData = useMemo(
    () => transformAuditResponse(data, shiftInfo),
    [data.edits, data.original, shiftInfo],
  );

  const modifiedShiftInfo = buildOriginalTimestamps(shiftInfo, data.original);

  return (
    <AuditTrailScrollableContainer>
      {/* Clock in and Clock out section */}
      <AuditClockInOutSection
        clockInOutEdits={transformedData.clockInOut}
        timezone={timezone}
        shiftThresholds={shiftThresholds}
        shiftKey={shiftKey}
        shiftInfo={modifiedShiftInfo}
      />

      {/* Meal break sections */}
      {transformedData.breaks.map((breakEdits, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={`break-${index}`}>
          <AuditBreakSection
            breakEdits={breakEdits}
            breakNumber={index + 1}
            timezone={timezone}
            shiftThresholds={shiftThresholds}
            shiftKey={shiftKey}
            shiftInfo={modifiedShiftInfo}
          />
        </React.Fragment>
      ))}
    </AuditTrailScrollableContainer>
  );
};
